import NextImage from "next/image";
import { TbShirtSport } from "react-icons/tb";
import { MdOutlinedFlag } from "react-icons/md";
import { HiOutlineTicket } from "react-icons/hi";
import { OpenInNewRounded } from "@mui/icons-material";
import { Box, Link, ListItemIcon, ListItemText, MenuItem } from "@mui/material";

import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

export const NewYorkYankeesLink = () => (
  <Link
    target="_blank"
    sx={{
      fontWeight: 500,
      color: "primary.main",
      textDecoration: "none",
    }}
    href="https://am.ticketmaster.com/nyyreg/"
  >
    Ballpark App
  </Link>
);

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose your games.",
    description: "Select the games you wish to attend.",
  },

  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description:
      "Tickets will be available in your Ballpark App. Reach out to Live Fan Support for any questions.",
  },
];

export const faqs = [
  {
    title: "When will I receive my tickets?",
    description:
      "If your reservation is accepted, a New York Yankees representative will be in contact with further information, including ticket delivery details.",
  },
  {
    title: "What happens after I purchase a New York Yankees Suite online?",
    description:
      "Following your purchase, a New York Yankees representative will follow up with you to confirm your order and assist with any questions you have in anticipation of your event. Tickets and parking will be added to your New York Yankees account where you'll be able to access them via the MLB Ballpark app. All suite tickets are digital.",
  },
  {
    title: "What is included in my suite?",
    description:
      "If your reservation is accepted, a New York Yankees representative will be in contact with further information, including details regarding parking and food and beverage options.",
  },
];

export function NewYorkYankeesCheckoutTerms() {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from rangers.com. You may unsubscribe at any time. By
      checking this box you agree to receive additional commercial emails from
      rangers.com and MLB.com. The terms and conditions of your ticket and
      policies applicable to its use are updated to address circumstances
      related to COVID-19 and health and safety. By clicking the Purchase
      Tickets button, you agree to accept all{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/astros/tickets/terms-and-conditions"
      >
        Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your New York Yankees account. Message and data rates may
      apply Tickets will be made available in the MLB Ballpark mobile
      application only.{" "}
      <Link
        target="_blank"
        href="http://mlb.com/"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
      >
        MLB.com
      </Link>{" "}
      and supported mobile device required
    </>
  );
}

export const NewYorkYankeesAdditionalDetailsDisclaimerTC = () => (
  <>
    By entering my name, checking the box above and clicking Submit Request, I
    hereby (i) agree to the{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/yankees/tickets/terms-and-conditions"
    >
      Terms of Use
    </Link>{" "}
    (ii) agree that my reservation will not be complete unless and until I
    receive a notice from the New York Yankees confirming whether or not my
    reservation has been accepted in accordance with our agreement.
  </>
);

export const NewYorkYankeesTCListItems = () => (
  <Box>
    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/yankees/tickets/terms-and-conditions",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>New York Yankees</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Privacy Policy</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Terms of Use</ListItemText>
    </MenuItem>
  </Box>
);

export const inventoryCardSuiteModal = createState({
  title: "",
  isOpen: false,
  suiteImage: "",
});

export const NewYorkYankeesInventoryCard = () => {
  const modal = inventoryCardSuiteModal();
  const { md } = useCssMediaQuery("down");

  if (!modal.suiteImage) return null;

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false, suiteImage: "" })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2, width: "auto" } }}
      renderBody={() => (
        <Box>
          <NextImage
            width={400}
            height={400}
            alt={"Suite image"}
            src={modal.suiteImage}
            style={{
              width: md ? "100%" : "500px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
      )}
    />
  );
};

export const inventoryCardSuitViewMenueModal = createState({
  isOpen: false,
  url: "",
});

export const NewYorkYankeesInventoryCardViewMenu = () => {
  const { md } = useCssMediaQuery("down");
  const modal = inventoryCardSuitViewMenueModal();
  const iframeUrl = `https://drive.google.com/viewerng/viewer?embedded=true&url=${encodeURIComponent(
    modal.url
  )}`;

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={"View Menu"}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false, url: "" })}
      paperProps={{
        style: {
          marginLeft: 2,
          marginRight: 2,
          width: "100%",
          height: "100%",
          maxWidth: md ? "95vw" : "80vw",
        },
      }}
      bodyProps={{
        style: {
          height: "100%",
        },
      }}
      renderBody={() => (
        <Box sx={{ height: "100%", maxHeight: "85vh" }}>
          <iframe
            className="pdf-iframe"
            src={iframeUrl}
            width="100%"
            height={"100%"}
          ></iframe>
        </Box>
      )}
    />
  );
};
